import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from '@/types/graph';
import { AnimatedProfessionalAvatar } from '@/components/AnimatedProfessionalAvatar';
import { useQuestionnairePages } from '@/hooks/useQuestionnairePages';
import { useResetQuestionnaire } from '@/hooks/useResetQuestionnaire';
import { Button } from '@/components/ui/button';
import StarsIcon2 from '@/assets/Offboarding/StarsIcon2.png';
import { isMmgVariant } from '@/util/mmg';
import { isKioskOnboarding } from '@/util/kiosk';
import { MmgConfirmation } from '@/pages/offboarding/MmgConfirmation';

export const DirectConfirmationPage = () => {
  const navigate = useNavigate();
  const resetQuestionnaire = useResetQuestionnaire();
  const [fadeIn, setFadeIn] = useState(false);
  const professional = useSelector(
    (state: RootState) => state.consultation.professional,
  );
  const currentPage = useSelector(
    (state: RootState) => state.graph.currentPage,
  );

  // mmg_borne (kiosk) reaches this DIRECT_CONFIRMATION offboarding; hand off to
  // the dedicated MMG check-in confirmation. Read the active questionnaire
  // variant (graph.variant) — the public `?variant=` path sets it without
  // updating consultation.variant (ALDO-2540).
  const isMmg = useSelector((state: RootState) =>
    isMmgVariant(state.graph.variant),
  );

  const isKiosk = useSelector((state: RootState) =>
    isKioskOnboarding(state.questionnaire.config?.patientOnboarding),
  );

  const { isLastPage } = useQuestionnairePages();

  useEffect(() => {
    if (!isLastPage) navigate('/questionnaire');
  }, [currentPage, isLastPage, navigate]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setFadeIn(true);
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  if (isMmg) {
    return <MmgConfirmation />;
  }

  return (
    <div
      className={`w-full h-full flex flex-col items-center justify-center text-base transition-opacity duration-600 ease-out px-4 n2xs:px-6 nsm:px-0 ${
        fadeIn ? 'opacity-100' : 'opacity-0'
      }`}
    >
      <div className="flex flex-col items-center justify-center h-full sm:w-full overflow-y-auto no-scrollbar">
        <div className="w-full max-w-md flex flex-col items-center text-center justify-between ">
          <div className="grow flex flex-col justify-center gap-2">
            <div
              className={`self-center flex flex-col shrink items-center justify-center z-10 transition-all duration-500 ease-out ${
                fadeIn ? 'opacity-100 scale-100' : 'opacity-0 scale-90'
              }`}
            >
              <AnimatedProfessionalAvatar
                professional={professional}
                size={160}
              />
            </div>
            <div
              className={`flex flex-col justify-start gap-4 mt-6 transition-all duration-500 ease-out delay-300 ${
                fadeIn ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
              }`}
            >
              <p className="text-xl font-medium px-10 mb-4">
                {`Merci pour vos réponses !`}
              </p>
            </div>

            <div
              className={`flex items-center gap-4 p-6 mx-6 rounded-lg transition-all duration-500 ease-out delay-[600ms] bg-[#DAF6F3] ${
                fadeIn ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
              }`}
            >
              <img src={StarsIcon2} alt="StarsIcon2" className="size-6" />
              <p className="font-light text-start leading-5">
                {`Vos réponses ont bien été`} <br />
                {`enregistrées et seront examinées`} <br />
                {`attentivement afin de mieux`} <br />
                {`comprendre votre situation.`}
              </p>
            </div>

            {isKiosk && (
              <div
                className={`mt-8 flex justify-center transition-all duration-500 ease-out delay-[900ms] ${
                  fadeIn
                    ? 'opacity-100 translate-y-0'
                    : 'opacity-0 translate-y-4'
                }`}
              >
                <Button
                  variant="ctaPrimary"
                  size="lg"
                  onClick={() => resetQuestionnaire()}
                >
                  Nouveau patient
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
